import { BrickWall, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import Spinner from "./Spinner";

interface Props {
  icon: LucideIcon;
  tone?: "default" | "warning" | "destructive" | "success";
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  fullPage?: boolean;
}

const TONES = {
  default: "bg-muted text-muted-foreground",
  warning: "bg-warning/10 text-warning",
  destructive: "bg-destructive/10 text-destructive",
  success: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
};

// A centred message with an icon, used for errors, empty routes and finished flows.
const StatusScreen: React.FC<Props> = ({
  icon: Icon,
  tone = "default",
  title,
  description,
  actions,
  fullPage = true,
}) => (
  <div
    className={cn(
      "flex flex-col items-center justify-center gap-4 p-6 text-center",
      fullPage ? "min-h-full" : "py-16",
    )}
  >
    <span className={cn("flex size-12 items-center justify-center rounded-xl", TONES[tone])}>
      <Icon className="size-6" />
    </span>
    <div className="grid max-w-md gap-1">
      <h1 className="text-lg font-semibold">{title}</h1>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
    {actions && <div className="flex flex-wrap justify-center gap-2">{actions}</div>}
  </div>
);

export const FullPageSpinner: React.FC = () => (
  <div className="flex min-h-full flex-col items-center justify-center gap-3 text-muted-foreground">
    <BrickWall className="size-6 text-primary" aria-hidden="true" />
    <Spinner />
  </div>
);

export default StatusScreen;
